import { PartsCatalogHit, PartsCatalogHitWire } from '@/types/quoteDocuments';

import { formatCurrency } from './currency';
import { parseNumberLike } from './normalizeQuoteDocument';

const trimmed = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const next = value.trim();
  return next ? next : null;
};

const KNOWN_PRICING_STATUSES: ReadonlySet<string> = new Set([
  'priced',
  'poa',
  'tbc',
  'indicative',
  'fallback',
]);

const pricingStatusFrom = (
  wire: PartsCatalogHitWire,
  unitSell: number | null
): PartsCatalogHit['pricingStatus'] => {
  const raw = wire.pricingStatus ?? wire.pricing_status;
  if (typeof raw === 'string' && KNOWN_PRICING_STATUSES.has(raw)) {
    return raw as PartsCatalogHit['pricingStatus'];
  }
  return unitSell != null && unitSell > 0 ? 'priced' : 'poa';
};

export const normalizePartsCatalogHit = (
  wire: PartsCatalogHitWire
): PartsCatalogHit => {
  const unitSell = parseNumberLike(wire.unitSell ?? wire.unit_sell);
  return {
    id: typeof wire.id === 'string' ? wire.id.trim() : '',
    title: trimmed(wire.title) ?? '',
    brand: trimmed(wire.brand),
    sku: trimmed(wire.sku),
    manufacturerSku: trimmed(wire.manufacturerSku ?? wire.manufacturer_sku),
    vendor: trimmed(wire.vendor),
    vendorSku: trimmed(wire.vendorSku ?? wire.vendor_sku),
    uom: trimmed(wire.uom),
    packQty: parseNumberLike(wire.packQty ?? wire.pack_qty),
    unitCost: parseNumberLike(wire.unitCost ?? wire.unit_cost),
    unitSell,
    currency: trimmed(wire.currency),
    pricingStatus: pricingStatusFrom(wire, unitSell),
    score: parseNumberLike(wire.score),
  };
};

/** Displayed part number: sku, else manufacturer, else vendor. Never the UUID. */
export const displayedPartNumber = (
  hit: Pick<PartsCatalogHit, 'sku' | 'manufacturerSku' | 'vendorSku'>
): string | null => hit.sku || hit.manufacturerSku || hit.vendorSku || null;

/** Price-book supplier (`vendor`). Title-cased for the picker. */
export const displayedSupplier = (
  vendor: PartsCatalogHit['vendor']
): string | null => {
  if (!vendor) return null;
  return vendor
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const partsCatalogSellLabel = (
  hit: Pick<PartsCatalogHit, 'pricingStatus' | 'unitSell'>
): string => {
  if (
    hit.pricingStatus === 'poa' ||
    hit.unitSell == null ||
    hit.unitSell <= 0
  ) {
    return 'POA';
  }
  return formatCurrency(hit.unitSell);
};

/** Picker sublabel: supplier · make · part number · sell or POA. Never the catalogue UUID. */
export const partsCatalogHitSublabel = (hit: PartsCatalogHit): string =>
  [
    displayedSupplier(hit.vendor),
    hit.brand,
    displayedPartNumber(hit),
    partsCatalogSellLabel(hit),
  ]
    .filter((part): part is string => Boolean(part))
    .join(' · ');

/** Keep the first hit per catalogue id. FSI candidates can repeat the same id. */
export const uniquePartsCatalogHits = (
  hits: PartsCatalogHit[]
): PartsCatalogHit[] => {
  const seen = new Set<string>();
  return hits.filter(hit => {
    if (seen.has(hit.id)) return false;
    seen.add(hit.id);
    return true;
  });
};

const auditText = (
  audit: Record<string, unknown> | null,
  key: string
): string | null => {
  if (!audit) return null;
  const value = audit[key];
  return trimmed(value);
};

/** Merchant name only. Never sku, brand, or the catalogue UUID. */
export const quoteLineSupplierLabel = (line: {
  audit: Record<string, unknown> | null;
}): string | null => displayedSupplier(auditText(line.audit, 'vendor'));

/** Displayed part number from the line audit. Never the catalogue UUID. */
export const quoteLinePartNumber = (line: {
  audit: Record<string, unknown> | null;
}): string | null => auditText(line.audit, 'sku');
